import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { useForm } from "react-hook-form";

const PAGE_SIZE = 20;
const CATEGORIES = ["OC", "BC", "BCM", "MBC", "MBCDNC", "MBCV", "SC", "SCA", "ST"];
const YEARS = ["2020", "2021", "2022", "2023", "2024"];

const COURSE_CATEGORIES = {
  "Computer Science": ["CS", "CM", "IM", "IT", "CB", "AD", "AL", "DS"],
  "Electronics": ["EC", "EM", "EI", "CO"],
  "Electrical": ["EE"],
  "Mechanical": ["ME", "MF", "XM", "AU", "AS", "MN", "PR", "RP"],
  "Civil": ["CE", "XC"],
  "Biomedical": ["BY", "BM"],
  "Chemical": ["CH", "CL", "CR", "FS", "IB", "IS", "LE", "PM", "PP", "TX"],
  "Other Engineering": ["AE", "GI", "IE", "MA", "MI", "PT", "FD", "RM", "MZ", "AG", "MM", "PH", "MD", "MU"]
};

export default function KnowMyCollege() {
  const { register, handleSubmit, watch, reset } = useForm({
    defaultValues: {
      cutoff: "",
      cutoffTolerance: 5,
      category: "OC",
      branchCode: [],
      branchCategory: "All",
      sortBy: "CollegeRank",
      year: "2024" // Default to most recent year
    },
  });

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [workbook, setWorkbook] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "CollegeRank",
    direction: "asc",
  });
  const [branchCodeOptions, setBranchCodeOptions] = useState([]);
  const [groupedBranchOptions, setGroupedBranchOptions] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  const watchCategory = watch("category");
  const watchSortBy = watch("sortBy");
  const watchYear = watch("year");

  useEffect(() => {
    const loadExcel = async () => {
      try {
        const response = await fetch("/TNEAUpdated.xlsx");
        const arrayBuffer = await response.arrayBuffer();
        const wb = XLSX.read(arrayBuffer, { type: "array" });
        setWorkbook(wb);

        // Initialize branch codes from the default year (2024)
        updateBranchCodes(wb, "2024");
      } catch (error) {
        console.error("Error loading Excel file:", error);
      }
    };

    loadExcel();
  }, []);

  const updateBranchCodes = (wb, year) => {
    const worksheet = wb.Sheets[year];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
    
    const branchSet = new Set();
    jsonData.forEach((row) => {
      if (row["Branch Code"]) {
        branchSet.add(row["Branch Code"].toString().trim());
      }
    });

    const allBranchCodes = Array.from(branchSet).sort();
    setBranchCodeOptions(allBranchCodes);

    const grouped = { ...COURSE_CATEGORIES };
    
    Object.keys(grouped).forEach(key => {
      grouped[key] = [];
    });

    allBranchCodes.forEach(code => {
      let found = false;
      
      Object.keys(COURSE_CATEGORIES).forEach(category => {
        COURSE_CATEGORIES[category].some(prefix => {
          if (code.startsWith(prefix)) {
            grouped[category].push(code);
            found = true;
            return true;
          }
          return false;
        });
      });

      if (!found) {
        grouped["Other Engineering"].push(code);
      }
    });

    setGroupedBranchOptions(grouped);
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const selectAllInGroup = (groupName, checked) => {
    const groupBranches = groupedBranchOptions[groupName] || [];
    const currentSelected = watch("branchCode") || [];
    
    if (checked) {
      // Add all branches in group that aren't already selected
      const newSelected = [...new Set([...currentSelected, ...groupBranches])];
      reset({
        ...watch(),
        branchCode: newSelected
      });
    } else {
      // Remove all branches in group
      const newSelected = currentSelected.filter(code => !groupBranches.includes(code));
      reset({
        ...watch(),
        branchCode: newSelected
      });
    }
  };

  const renderBranchSelection = () => {
    return (
      <div className="border rounded p-2 bg-white">
        <label className="block mb-1">Branch Codes</label>
        <div className="max-h-60 overflow-y-auto">
          {Object.keys(groupedBranchOptions).map(group => (
            <div key={group} className="mb-2">
              <div className="flex items-center justify-between bg-gray-100 p-1 rounded">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={(watch("branchCode") || []).some(code => 
                      groupedBranchOptions[group].includes(code)
                    )}
                    onChange={(e) => selectAllInGroup(group, e.target.checked)}
                    className="mr-2"
                  />
                  <span className="font-medium">{group}</span>
                </label>
                <button 
                  type="button" 
                  onClick={() => toggleGroup(group)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  {expandedGroups[group] ? '−' : '+'}
                </button>
              </div>
              {expandedGroups[group] && (
                <div className="ml-4 mt-1 grid grid-cols-2 sm:grid-cols-3 gap-1">
                  {groupedBranchOptions[group].map(code => (
                    <label key={code} className="flex items-center">
                      <input
                        type="checkbox"
                        {...register("branchCode")}
                        value={code}
                        checked={(watch("branchCode") || []).includes(code)}
                        className="mr-1"
                      />
                      <span className="text-sm">{code}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    setResults(prevResults => {
      const sorted = [...prevResults].sort((a, b) => {
        const aVal = a[key];
        const bVal = b[key];
        
        if (aVal < bVal) {
          return direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
      return sorted;
    });
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const onSubmit = (data) => {
    const { cutoff, cutoffTolerance, category, branchCode, sortBy, year } = data;

    if (!workbook) {
      alert("Excel file not loaded yet.");
      return;
    }

    setLoading(true);
    setResults([]);
    setCurrentPage(1);

    try {
      const worksheet = workbook.Sheets[year];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

      const cutoffNum = parseFloat(cutoff);
      const tolNum = parseFloat(cutoffTolerance);

      const filteredRows = jsonData.filter((row) => {
        // Filter by branch code if any are selected
        if (branchCode.length > 0 && !branchCode.includes(row["Branch Code"]?.toString().trim())) {
          return false;
        }

        // Filter by cutoff - include all colleges with cutoff <= (cutoff + tolerance)
        const categoryVal = parseFloat(row[category]);
        if (isNaN(categoryVal)) return false;

        return categoryVal <= (cutoffNum + tolNum);
      });

      // Sort results - prioritize colleges with cutoff closest to the target first
      filteredRows.sort((a, b) => {
        // First sort by the selected column (rank)
        const aSortVal = parseFloat(a[sortBy]);
        const bSortVal = parseFloat(b[sortBy]);
        
        if (!isNaN(aSortVal) && !isNaN(bSortVal)) {
          if (aSortVal !== bSortVal) {
            return aSortVal - bSortVal;
          }
        }

        // Then sort by how close the cutoff is to the target
        const aDiff = Math.abs(parseFloat(a[category]) - cutoffNum);
        const bDiff = Math.abs(parseFloat(b[category]) - cutoffNum);
        
        return aDiff - bDiff;
      });

      setResults(filteredRows);
      setSortConfig({ key: sortBy, direction: "asc" });
    } catch (error) {
      console.error("Error processing data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (e) => {
    const selectedYear = e.target.value;
    reset({
      ...watch(),
      year: selectedYear
    });
    updateBranchCodes(workbook, selectedYear);
  };

  const totalPages = Math.ceil(results.length / PAGE_SIZE);
  const paginatedResults = results.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div className="bg-gray-100 py-12 mt-28">
      <div className="max-w-7xl mx-auto p-6">
        <h2 className="text-3xl font-bold text-blue-700 mb-6">
          Yaazhtech's Suggestions and Analytical view
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
          <div>
            <label className="block mb-1">Year</label>
            <select 
              {...register("year")} 
              onChange={handleYearChange}
              className="w-full border rounded px-2 py-1"
            >
              {YEARS.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1">Your Cutoff</label>
            <input 
              type="number" 
              step="0.1" 
              {...register("cutoff", { required: true })} 
              className="w-full border rounded px-2 py-1" 
              placeholder="Enter your cutoff"
            />
          </div>

          <div>
            <label className="block mb-1">Tolerance (±)</label>
            <input 
              type="number" 
              {...register("cutoffTolerance")} 
              className="w-full border rounded px-2 py-1" 
              defaultValue={5}
              min={0}
              max={50}
            />
          </div>

          <div>
            <label className="block mb-1">Category</label>
            <select 
              {...register("category")} 
              className="w-full border rounded px-2 py-1"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1">Sort By</label>
            <select 
              {...register("sortBy")} 
              className="w-full border rounded px-2 py-1"
            >
              <option value="CollegeRank">College Rank</option>
              <option value="CollegeRankByCourseAverage">Course Average Rank</option>
              <option value="CourseAverage">Course Average</option>
              <option value="OverallAverage">Overall Average</option>
            </select>
          </div>

          <div className="md:col-span-2">
            {renderBranchSelection()}
          </div>

          <div className="col-span-full flex gap-4 mt-2">
            <button 
              type="submit" 
              className="bg-blue-600 text-white rounded px-4 py-2 hover:bg-blue-800"
              disabled={loading}
            >
              {loading ? "Searching..." : "Find Colleges"}
            </button>
            <button 
              type="button" 
              onClick={() => {
                reset();
                setResults([]);
                setCurrentPage(1);
                setSortConfig({ key: "CollegeRank", direction: "asc" });
                setExpandedGroups({});
              }} 
              className="text-gray-600 hover:underline"
            >
              Reset
            </button>
          </div>
        </form>

        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-blue-500">Loading results...</p>
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg shadow">
            <h3 className="text-lg font-medium text-gray-700">No colleges found</h3>
            <p className="mt-1 text-gray-500">
              {workbook 
                ? "Try adjusting your cutoff or increasing the tolerance"
                : "Loading college data..."}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600">
                  Showing {results.length} colleges matching your criteria for {watchYear}
                </div>
                <div className="text-sm font-medium">
                  Cutoff range: ≤ {watchCategory} {parseFloat(watch("cutoff")) + parseFloat(watch("cutoffTolerance"))}
                </div>
              </div>
            </div>
            
            <div className="overflow-auto">
              <table className="min-w-full divide-y divide-gray-200">
               <thead className="bg-gray-50">
  <tr>
    <th 
      scope="col" 
      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer w-16"
      onClick={() => handleSort("CollegeRank")}
    >
      Rank {renderSortArrow("CollegeRank")}
    </th>
    <th 
      scope="col" 
      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer min-w-[120px] max-w-[200px]"
      onClick={() => handleSort("College Name")}
    >
      <div className="truncate">College Name</div>
      {renderSortArrow("College Name")}
    </th>
    <th 
      scope="col" 
      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32"
    >
      College Code
    </th>
    <th 
      scope="col" 
      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer min-w-[150px]"
      onClick={() => handleSort("Branch Code")}
    >
      Branch {renderSortArrow("Branch Code")}
    </th>
    <th 
      scope="col" 
      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer w-32"
      onClick={() => handleSort(watchCategory)}
    >
      <div className="truncate">{watchCategory} Cutoff</div>
      {renderSortArrow(watchCategory)}
    </th>
    <th 
      scope="col" 
      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer w-32"
      onClick={() => handleSort(watchSortBy)}
    >
      <div className="truncate">{watchSortBy}</div>
      {renderSortArrow(watchSortBy)}
    </th>
  </tr>
</thead>
<tbody className="bg-white divide-y divide-gray-200">
  {paginatedResults.map((row, idx) => (
    <tr key={idx} className="hover:bg-gray-50">
      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 w-16">
        {row["CollegeRank"]}
      </td>
      <td className="px-3 py-4 min-w-[120px] max-w-[200px]">
        <div className="text-sm font-medium text-gray-900 truncate" title={row["College Name"]}>
          {row["College Name"]}
        </div>
      </td>
      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 w-32">
        {row["College Code"]}
      </td>
      <td className="px-3 py-4 min-w-[150px]">
        <div className="text-sm font-medium text-gray-900 truncate" title={row["Branch Code"]}>
          {row["Branch Code"]}
        </div>
        <div className="text-sm text-gray-500 truncate" title={row["Branch Name"]}>
          {row["Branch Name"]}
        </div>
      </td>
      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 w-32">
        {row[watchCategory]}
      </td>
      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 w-32">
        {row[watchSortBy]}
      </td>
    </tr>
  ))}
</tbody>
               
              </table>
            </div>

            {totalPages > 1 && (
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * PAGE_SIZE, results.length)}</span> of{' '}
                      <span className="font-medium">{results.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <span className="sr-only">First</span>
                        «
                      </button>
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <span className="sr-only">Previous</span>
                        ‹
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNum
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <span className="sr-only">Next</span>
                        ›
                      </button>
                      <button
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <span className="sr-only">Last</span>
                        »
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}